"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronDownIcon } from "@heroicons/react/24/outline";
import clsx from "clsx";

import { useTradeStore, type MarketId } from "@/stores/tradeStore";
import { MARKET_CONFIG, fetchHyperliquidMids } from "@/lib/hyperliquid";
import { useAssets } from "@/hooks/useAssets";
import type { AssetClass, AssetDto } from "@/lib/api";

type MarketRow = {
  id: MarketId;
  label: string;
  displaySymbol: string;
  price: number;
  loading: boolean;
};

type MarketCategory = {
  name: string;
  markets: MarketRow[];
};

const ASSET_CLASS_CATEGORY: Record<AssetClass, string> = {
  crypto: "Crypto",
  future: "Futures",
  future_option: "Futures Options",
  equity: "Equities",
};

const CATEGORY_ORDER = ["Crypto", "Futures", "Futures Options", "Equities"];

/**
 * SFWL-004: Pull initial mid prices from Hyperliquid for every asset whose
 * symbol still has an entry in the static MARKET_CONFIG mapping. Once
 * SFWL-005 lands the provider framework this becomes a per-asset provider
 * dispatch.
 */
async function loadInitialMids(symbols: MarketId[]): Promise<Record<MarketId, number>> {
  const knownSymbols = symbols.filter((s) => MARKET_CONFIG[s]);
  if (knownSymbols.length === 0) return {};

  const dexes = new Set<string>();
  for (const s of knownSymbols) {
    dexes.add(MARKET_CONFIG[s].dex ?? "default");
  }

  const dexList = Array.from(dexes);
  const midSets = await Promise.all(
    dexList.map((dex) => fetchHyperliquidMids(dex === "default" ? undefined : dex))
  );

  const midsByDex: Record<string, Record<string, string>> = {};
  dexList.forEach((dex, i) => {
    midsByDex[dex] = midSets[i];
  });

  const result: Record<MarketId, number> = {};
  for (const s of knownSymbols) {
    const cfg = MARKET_CONFIG[s];
    const mids = midsByDex[cfg.dex ?? "default"];
    const mid = mids?.[cfg.coin];
    if (mid) result[s] = parseFloat(mid);
  }
  return result;
}

interface PriceSnapshot {
  symbols: ReadonlySet<string>;
  prices: Record<string, number>;
}

const EMPTY_SNAPSHOT: PriceSnapshot = { symbols: new Set(), prices: {} };

export const MarketSelector = () => {
  const { market, setMarket } = useTradeStore();
  const livePrices = useTradeStore((s) => s.livePrices);
  const { assets, isLoading: assetsLoading } = useAssets();
  const [open, setOpen] = useState(false);
  // Single snapshot keyed by the asset set we fetched for. Replacing this in
  // one shot (only inside the async callback) avoids the React 19
  // `react-hooks/set-state-in-effect` lint that fires on synchronous
  // setState in the effect body.
  const [priceSnapshot, setPriceSnapshot] = useState<PriceSnapshot>(EMPTY_SNAPSHOT);

  // Default the selected market once assets arrive if the persisted value isn't valid
  useEffect(() => {
    if (assets.length === 0) return;
    if (!assets.find((a) => a.symbol === market)) {
      setMarket(assets[0].symbol);
    }
  }, [assets, market, setMarket]);

  // Fetch initial prices from Hyperliquid once we know the universe
  useEffect(() => {
    if (assets.length === 0) return;
    let cancelled = false;
    const symbols = assets.map((a) => a.symbol);

    loadInitialMids(symbols)
      .then((prices) => {
        if (!cancelled) setPriceSnapshot({ symbols: new Set(symbols), prices });
      })
      .catch((err) => {
        console.error("[MarketSelector] Failed to fetch mids:", err);
        if (!cancelled) setPriceSnapshot({ symbols: new Set(symbols), prices: {} });
      });

    return () => {
      cancelled = true;
    };
  }, [assets]);

  // Compose rows from API assets + live + initial prices
  const markets = useMemo<MarketRow[]>(() => {
    return assets.map((a) => ({
      id: a.symbol,
      label: marketLabel(a),
      displaySymbol: a.displaySymbol,
      price: livePrices[a.symbol] ?? priceSnapshot.prices[a.symbol] ?? 0,
      loading: !priceSnapshot.symbols.has(a.symbol) && livePrices[a.symbol] === undefined,
    }));
  }, [assets, livePrices, priceSnapshot]);

  const activeMarket = markets.find((m) => m.id === market) ??
    markets[0] ?? {
      id: "",
      label: "",
      displaySymbol: "",
      price: 0,
      loading: assetsLoading,
    };

  const categories = useMemo<MarketCategory[]>(() => {
    const grouped = new Map<string, MarketRow[]>();
    assets.forEach((a, i) => {
      const name = ASSET_CLASS_CATEGORY[a.assetClass] ?? "Other";
      if (!grouped.has(name)) grouped.set(name, []);
      grouped.get(name)!.push(markets[i]);
    });

    return CATEGORY_ORDER.filter((name) => grouped.has(name)).map((name) => ({
      name,
      markets: grouped.get(name) ?? [],
    }));
  }, [assets, markets]);

  return (
    <div className="relative inline-block">
      <button
        className="flex items-center gap-3 rounded-xl border border-slate-200/60 bg-white/10 px-4 py-2 text-left shadow-sm backdrop-blur transition hover:border-slate-300/60"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={assetsLoading && markets.length === 0}
      >
        <div className="flex items-center gap-2">
          <p className="text-sm font-semibold text-white">
            {activeMarket.id || (assetsLoading ? "Loading…" : "—")}
          </p>
          {activeMarket.displaySymbol && (
            <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs font-medium text-slate-200">
              {activeMarket.displaySymbol}
            </span>
          )}
        </div>
        <ChevronDownIcon className="h-5 w-5 text-slate-300" />
      </button>

      {open && categories.length > 0 ? (
        <div className="absolute z-20 mt-2 w-72 rounded-2xl border border-slate-200 bg-white p-2 shadow-lg">
          <div role="listbox">
            {categories.map((cat) => (
              <div key={cat.name} className="mb-1 last:mb-0">
                <p className="px-3 pb-1 pt-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">
                  {cat.name}
                </p>
                {cat.markets.map((m) => (
                  <button
                    key={m.id}
                    onClick={() => {
                      setMarket(m.id);
                      setOpen(false);
                    }}
                    className={clsx(
                      "flex w-full items-center justify-between rounded-xl px-3 py-2 text-left transition hover:bg-slate-50",
                      m.id === market ? "bg-slate-100" : ""
                    )}
                    role="option"
                    aria-selected={m.id === market}
                  >
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-sm font-semibold text-slate-900">{m.id}</p>
                        <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">
                          {m.displaySymbol}
                        </span>
                      </div>
                      <p className="text-xs text-slate-600">
                        {m.loading ? (
                          <span className="animate-pulse">Loading...</span>
                        ) : m.price > 0 ? (
                          `$${m.price.toLocaleString(undefined, { maximumFractionDigits: 2 })}`
                        ) : (
                          "—"
                        )}
                      </p>
                    </div>
                    {!m.loading && m.price > 0 && (
                      <span className="text-xs font-medium text-slate-500">
                        {m.label}
                      </span>
                    )}
                  </button>
                ))}
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
};

/**
 * Human-friendly short label derived from the asset row (e.g. "S&P 500" for ES).
 * Falls back to "Perp" for crypto so the existing demo styling holds.
 */
function marketLabel(asset: AssetDto): string {
  if (asset.assetClass === "crypto") return "Perp";
  const match = asset.displaySymbol.match(/^([^()]+?)\s*\(/);
  return match?.[1]?.trim() ?? asset.displaySymbol;
}
